<template>
    <!-- Header -->
    <div class="flex justify-between items-center pb-3">
        <h4 class="font-bold mb-0">{{ t('users.title') }}</h4>
        <Button :icon="viewMode === 'table' ? 'fa-solid fa-grip' : 'fa-solid fa-table'" @click="toggleView" outlined />
    </div>
    <div class="pb-4">
        <!-- Card View -->
        <UserCardView
            v-if="viewMode === 'card'"
            :users="users"
            :loading="loading"
            :search="cardSearch"
            :status-filter="cardStatusFilter"
            :first="cardFirst"
            :rows="cardPageSize"
            :total-records="cardMetadata?.filtered || 0"
            @search="onCardSearch"
            @status-change="onCardStatusChange"
            @page="onCardPage"
            @edit="onEditUser"
        />

        <!-- Table View -->
        <UserTableView
            v-else
            :users="users"
            :loading="loading"
            :filters="tableFilters"
            :sort-field="tableSortField"
            :sort-order="tableSortOrder"
            :first="tableFirst"
            :rows="tablePageSize"
            :total-records="tableMetadata?.filtered || 0"
            @page="onTablePage"
            @filter="onTableFilter"
            @sort="onTableSort"
            @clear-filters="clearTableFilters"
            @edit="onEditUser"
        />

        <!-- Modal -->
        <UserEditDialog :visible="userModal.isOpen.value" mode="edit" :user="userModal.data.value" @close="userModal.close" @saved="onUserSaved" />
    </div>
</template>

<script setup lang="ts">
import UserEditDialog from '@/components/modal/User.vue';
import UserCardView from '@/components/users/UserCardView.vue';
import UserTableView from '@/components/users/UserTableView.vue';
import { useModal } from '@/composables/useModal';
import { useUsersPage } from '@/composables/useUsersPage';
import type { User } from '@/lib/types/user';
import { getInitialViewMode, ViewModeKey } from '@/utils/datatable';
import { useToast } from 'primevue/usetoast';
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const toast = useToast();

// Modal
const userModal = useModal<User>();

// Page logic
const {
    // State
    viewMode,
    loading,
    users,

    // Table
    tableFilters,
    tableSortField,
    tableSortOrder,
    tableFirst,
    tablePageSize,
    tableMetadata,

    // Card
    cardSearch,
    cardStatusFilter,
    cardFirst,
    cardPageSize,
    cardMetadata,

    // Methods
    toggleView,
    fetchData,
    initialize,
    onTablePage,
    onTableFilter,
    onTableSort,
    clearTableFilters,
    onCardPage,
    filterCards
} = useUsersPage(getInitialViewMode(ViewModeKey.USERS));

// Card view handlers with debouncing
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

function onCardSearch(value: string) {
    cardSearch.value = value;

    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
        filterCards();
    }, 500);
}

function onCardStatusChange(value: boolean | null) {
    cardStatusFilter.value = value;
    filterCards();
}

function onEditUser(user: User) {
    if (!user.editable) return;
    userModal.open('edit', user);
}

// Event handlers
function onUserSaved() {
    fetchData();
    toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('users.saveSuccess'),
        life: 3000
    });
}

// Lifecycle
onMounted(async () => {
    await initialize();
});
</script>
